import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  AfterViewInit,
  Renderer2,
  inject,
  PLATFORM_ID,
  ElementRef,
  input,
  output
} from '@angular/core';

import { LanguageService, WindowRef } from '@spartacus/core';
import { SpinnerModule } from '@spartacus/storefront';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { DpSiteContextService } from '@app-modules/site-context/services';
import { environment } from '@app-src/environments/environment';

import { CustomWindow, DataContext } from './types/customer-voice.types';

@Component({
  selector: 'app-customer-voice',
  templateUrl: './customer-voice.component.html',
  imports: [SpinnerModule],
  styleUrls: ['./customer-voice.component.scss']
})
export class DpCustomerVoiceComponent implements AfterViewInit {
  displayInline = input<boolean>(true);
  closed = output();

  readonly surveyScriptUrl = 'https://mfpembedcdnweu.azureedge.net/mfpembedcontweu/Embed.js';
  readonly surveyCssUrl = 'https://mfpembedcdnweu.azureedge.net/mfpembedcontweu/Embed.css';
  readonly surveyBaseUrl = 'https://customervoice.microsoft.com/';
  readonly surveyEmbedUrl = 'https://mfpembedcdnweu.azureedge.net/mfpembedcontweu/';

  readonly siteContextService$ = inject(DpSiteContextService);
  readonly #languageService = inject(LanguageService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #renderer = inject(Renderer2);
  readonly #elRef = inject(ElementRef);
  readonly #windowRef = inject(WindowRef);

  get surveyId(): string {
    return this.displayInline() ? environment.surveyIdConfirmationPage ?? '' : environment.surveyIdPopup ?? '';
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.#platformId)) {
      this.#loadSurveyResources();
    }
  }

  #loadSurveyResources(): void {
    const doc: Document = this.#elRef.nativeElement.ownerDocument;

    this.#loadStyle(doc);
    this.#loadScript(doc)
      .then(() => {
        this.#renderSurvey();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  #loadStyle(doc: Document): void {
    if (!doc.querySelector(`link[href="${this.surveyCssUrl}"]`)) {
      const link = this.#renderer.createElement('link');
      this.#renderer.setAttribute(link, 'rel', 'stylesheet');
      this.#renderer.setAttribute(link, 'href', this.surveyCssUrl);
      this.#renderer.appendChild(doc.head, link);
    }
  }

  #loadScript(doc: Document): Promise<void> {
    return new Promise((resolve, reject) => {
      if (doc.querySelector(`script[src="${this.surveyScriptUrl}"]`)) {
        resolve();
        return;
      }

      const script = this.#renderer.createElement('script');
      this.#renderer.setAttribute(script, 'src', this.surveyScriptUrl);
      this.#renderer.setAttribute(script, 'type', 'text/javascript');
      script.onload = (): void => {
        resolve();
      };
      script.onerror = (): void => {
        reject(new Error('SurveyEmbed script loading error'));
      };
      this.#renderer.appendChild(doc.body, script);
    });
  }

  #renderSurvey(): void {
    const win = this.#windowRef.nativeWindow as CustomWindow;
    const SurveyEmbed = win?.SurveyEmbed;

    if (!SurveyEmbed) {
      return;
    }

    combineLatest([
      this.#languageService.getActive(),
      this.siteContextService$.getActiveCountry$()
    ])
      .pipe(take(1))
      .subscribe(([locale, country]) => {
        const se = new SurveyEmbed(
          this.surveyId,
          this.surveyBaseUrl,
          this.surveyEmbedUrl,
          'true'
        );

        const context: DataContext = {
          locale,
          country: country?.market,
        };

        if (this.displayInline()) {
          se.renderInline('surveyDiv', context);
        } else {
          context.pageUrl = this.#windowRef.location.href;
          se.renderPopup(context);
          const observer = new MutationObserver(() => {
            const iframe = this.#windowRef.document.querySelector('iframe[src*="customervoice.microsoft.com"]');
            if (!iframe) {
              this.closed.emit();
              observer.disconnect();
            }
          });

          observer.observe(this.#windowRef.document.body, {
            childList: true,
            subtree: true
          });
        }
      });
  }
}
